import axios from "axios";
import { WarningRounded } from "@material-ui/icons";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";

import {
  cepMask,
  cnpjMask,
  cpfMask,
  phoneMask
} from "../../../../_metronic/_helpers/MasksHelper";
import { JdsButton } from "../../../components/jds/JdsButton";
import { JdsTag } from "../../../components/jds/JdsTag";
import { jdsSelectProps } from "../../../components/jds/selectProps";
import "../../../components/jds/jds.css";
import "../adminClientes.css";
import { PAYMENT_PROVIDERS } from "../../../constants/ClientesDados";
import {
  COURTESY_PRODUCT_OPTIONS,
  COURTESY_JUSSUB_DURATION_OPTIONS,
  COURTESY_MIN_DAYS,
  COURTESY_MAX_DAYS
} from "../../../constants/courtesy";
import { normalizeAmount } from "../../../helpers/normalizeAmount";
import ModalConfirm from "../../../components/ModalConfirm";
import { PlansAPI } from "../../../api/payments/plans/api";

// Rótulo acima do campo; obrigatório leva asterisco vermelho depois do texto.
const Label = ({ required, children, ...rest }) => (
  <label {...rest}>
    {children}
    {required && (
      <span className="text-danger" aria-hidden="true">
        {" "}*
      </span>
    )}
  </label>
);

// Erro abaixo do campo.
const FieldError = ({ show, children }) =>
  show ? <div className="invalid-feedback d-block">{children}</div> : null;

export default function Step1(props) {
  const dispatch = useDispatch();
  const states = useSelector(state => state.app.states);
  const cities = useSelector(state => state.app.cities);
  const professions = useSelector(state => state.app.professions);
  const { user } = useSelector(state => state.auth);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToRegister, setIsValidToRegister] = useState(false);
  const [isPaidFlowWarningOpen, setIsPaidFlowWarningOpen] = useState(false);

  const loadCidades = (state, city = "") => {
    dispatch({
      type: "LOAD_CITIES",
      payload: {
        state: state,
        callback: _cities => {
          if (city !== "") {
            setTimeout(() => {
              const found_city = _cities.find(
                value_city => value_city.name === city
              );

              if (found_city) {
                props.formik.setFieldValue("city", found_city);
              }
            }, 1500);
          }
        }
      }
    });
  };
  const chooseCEP = async cep => {
    if (!cep || cep.replace(/\D/g, "").length < 8) {
      return;
    }
    var request = new XMLHttpRequest();
    request.open(
      "get",
      `https://viacep.com.br/ws/${cep.replace(/\D/g, "")}/json/`,
      true
    );
    request.send();
    request.onload = function() {
      const result = JSON.parse(this.response);
      let state = states.find(state => state.state === result.uf);
      if (state) {
        props.formik.setFieldValue("state", state);
        props.formik.setFieldValue("address", result.logradouro);
        props.formik.setFieldValue("district", result.bairro);

        loadCidades(state.state, result.localidade);
      }
    };
  };

  const debouncedFilter = useCallback(
    debounce(inputValue => {
      if (inputValue) {
        const filtered = plans.filter(plan =>
          plan.name.toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredPlans(filtered);
      } else {
        setFilteredPlans(plans.slice(0, 10));
      }
    }, 300),
    [plans]
  );

  useEffect(() => {
    let cancelled = false;

    const getPlans = async () => {
      try {
        setIsLoading(true);

        if (props.useJusSub) {
          const response = await PlansAPI.list();
          const plansList = response.data?.content || [];
          if (!cancelled) {
            setPlans(plansList);
            setFilteredPlans(plansList.slice(0, 10));
          }
          return;
        }

        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/subscription/plans_admin"
        );
        if (!cancelled && response.data?.plans) {
          const plansList = response.data.plans;
          setPlans(plansList);
          setFilteredPlans(plansList.slice(0, 10));
        }
      } catch (err) {
        // no fluxo JusSub o erro já é exibido via toast pela camada de API
        if (!cancelled && !props.useJusSub) {
          toast.error("Ocorreu um erro ao carregar os planos.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    getPlans();

    return () => {
      cancelled = true;
    };
  }, [props.useJusSub]);

  const MIN_VALID_PLAN_AMOUNT = 500;

  const getNormalizedPlanAmount = value => {
    let normalizedAmount = normalizeAmount(value.amount);
    if (props.useJusSub) {
      normalizedAmount = Math.round(value.amount * 100); // planos JusSub retornam valor em reais; converte para centavos uma única vez
    }
    return normalizedAmount;
  };

  const handleOnChangePlan = value => {
    const normalizedAmount = getNormalizedPlanAmount(value);

    if (normalizedAmount === 0) {
      toast.error(
        "Erro ao processar o valor do plano. Verifique se o valor está correto."
      );
      setIsValidToRegister(false);
      props.formik.setFieldError("plan", "Valor do plano inválido.");
      return;
    }

    props.formik.setFieldValue("plan", value);
    if (normalizedAmount < MIN_VALID_PLAN_AMOUNT) {
      toast.error(
        `Não foi possível realizar a operação. Valor mínimo permitido para boletos: R$${Number(
          MIN_VALID_PLAN_AMOUNT / 100
        ).toFixed(2)}`
      );
      setIsValidToRegister(false);
      props.formik.setFieldError("plan", "O valor do plano está abaixo do mínimo permitido.");
      return;
    }
    setIsValidToRegister(true);
    props.formik.setFieldError("plan", undefined);
  };

  useEffect(() => {
    if (props.formik.values.is_courtesy) {
      return;
    }
    const plan = props.formik.values.plan;
    setIsValidToRegister(
      Boolean(plan) && getNormalizedPlanAmount(plan) >= MIN_VALID_PLAN_AMOUNT
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formik.values.is_courtesy]);

  const handleCadastrarClick = async () => {
    const errors = await props.formik.validateForm();
    if (Object.keys(errors).length > 0) {
      props.formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      toast.error(Object.values(errors)[0] || "Existem campos obrigatórios não preenchidos.");
      return;
    }

    if (!props.formik.values.is_courtesy) {
      setIsPaidFlowWarningOpen(true);
      return;
    }
    await props.formik.handleSubmit();
  };

  const canSubmit = props.formik.values.is_courtesy
    ? Boolean(props.formik.values.courtesy_product)
    : isValidToRegister;

  const formik = props.formik;
  const hasError = name => Boolean(formik.errors[name] && formik.touched[name]);
  const selectClass = name =>
    "ac-select" + (hasError(name) ? " is-invalid" : "");
  const inputClass = name =>
    "form-control" + (hasError(name) ? " is-invalid" : "");

  return (
    <BlockUi tag="div" blocking={formik.isSubmitting}>
      <h1 className="jds-title-lg ac-title">Cadastrar cliente</h1>
      <div className="jds-card ac-card">
        <section className="ac-section">
          <h2 className="jds-title-sm ac-section-title">Dados do cliente</h2>
          <div className="ac-grid">
            <div className="form-group">
              <Label htmlFor="profession" required>
                Profissão
              </Label>
              <Select
                {...jdsSelectProps}
                inputId="profession"
                name="profession"
                isSearchable={true}
                isClearable={true}
                options={professions}
                className={selectClass("profession")}
                onChange={value => {
                  formik.setFieldValue("profession", value);
                }}
                value={formik.values.profession}
                placeholder="Selecione"
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                noOptionsMessage={() => <span>Sem resultados.</span>}
              />
              <FieldError show={hasError("profession")}>
                {formik.errors.profession}
              </FieldError>
            </div>
            {formik.values.profession != null &&
              formik.values.profession.name === "Advogado" && (
              <>
                <div className="form-group">
                  <Label htmlFor="oab">Nº da OAB</Label>
                  <input
                    id="oab"
                    type="number"
                    name="oab"
                    className={inputClass("oab")}
                    value={formik.values.oab}
                    onChange={formik.handleChange}
                  />
                  <FieldError show={hasError("oab")}>
                    {formik.errors.oab}
                  </FieldError>
                </div>
                <div className="form-group">
                  <Label htmlFor="oab_state">Estado da OAB</Label>
                  <Select
                    {...jdsSelectProps}
                    inputId="oab_state"
                    name="oab_state"
                    isSearchable={true}
                    isClearable={true}
                    options={states}
                    className={selectClass("oab_state")}
                    onChange={value => {
                      formik.setFieldValue("oab_state", value);
                    }}
                    value={formik.values.oab_state}
                    placeholder="Selecione"
                    getOptionLabel={option => option.state}
                    getOptionValue={option => option.state}
                    noOptionsMessage={() => <span>Sem resultados.</span>}
                  />
                  <FieldError show={hasError("oab_state")}>
                    {formik.errors.oab_state}
                  </FieldError>
                </div>
              </>
            )}
            <div className="form-group">
              <Label htmlFor="name" required>
                Nome completo
              </Label>
              <input
                id="name"
                type="text"
                name="name"
                className={inputClass("name")}
                value={formik.values.name}
                onChange={formik.handleChange}
              />
              <FieldError show={hasError("name")}>
                {formik.errors.name}
              </FieldError>
            </div>
            <div className="form-group">
              <Label id="type_label" required>
                Tipo
              </Label>
              <div
                className="ac-radios"
                role="radiogroup"
                aria-labelledby="type_label"
              >
                <Form.Check
                  type="radio"
                  label="Pessoa física"
                  name="type"
                  id="type_pf"
                  checked={formik.values.type === "PF"}
                  onClick={() => {
                    formik.setFieldValue("type", "PF");
                    formik.setFieldValue("document_number", "");
                  }}
                />
                <Form.Check
                  type="radio"
                  label="Pessoa jurídica"
                  name="type"
                  id="type_pj"
                  checked={formik.values.type === "PJ"}
                  onClick={() => {
                    formik.setFieldValue("type", "PJ");
                    formik.setFieldValue("document_number", "");
                  }}
                />
              </div>
              <FieldError show={hasError("type")}>
                {formik.errors.type}
              </FieldError>
            </div>
            <div className="form-group">
              <Label id="client_type_label" required>
                Segmento
              </Label>
              <div
                className="ac-radios"
                role="radiogroup"
                aria-labelledby="client_type_label"
              >
                <Form.Check
                  type="radio"
                  label="B2C"
                  name="client_type"
                  id="client_type_b2c"
                  checked={formik.values.client_type === "b2c"}
                  onClick={() => {
                    formik.setFieldValue("client_type", "b2c");
                  }}
                />
                <Form.Check
                  type="radio"
                  label="B2B"
                  name="client_type"
                  id="client_type_b2b"
                  checked={formik.values.client_type === "b2b"}
                  onClick={() => {
                    formik.setFieldValue("client_type", "b2b");
                  }}
                />
              </div>
              <FieldError show={hasError("client_type")}>
                {formik.errors.client_type}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="document_number" required>
                CPF/CNPJ
              </Label>
              <input
                id="document_number"
                type="text"
                name="document_number"
                className={inputClass("document_number")}
                value={formik.values.document_number}
                onChange={evt => {
                  formik.setFieldValue(
                    "document_number",
                    formik.values.type === "PJ"
                      ? cnpjMask(evt.target.value)
                      : cpfMask(evt.target.value)
                  );
                }}
              />
              <FieldError show={hasError("document_number")}>
                {formik.errors.document_number}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="email" required>
                E-mail
              </Label>
              <input
                id="email"
                type="text"
                name="email"
                className={inputClass("email")}
                value={formik.values.email}
                onChange={e => {
                  const lowerCaseValue = e.target.value.toLowerCase();
                  formik.setFieldValue("email", lowerCaseValue);
                }}
              />
              <FieldError show={hasError("email")}>
                {formik.errors.email}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="phone" required>
                Telefone
              </Label>
              <input
                id="phone"
                type="text"
                name="phone"
                className={inputClass("phone")}
                value={formik.values.phone}
                onChange={evt =>
                  formik.setFieldValue("phone", phoneMask(evt.target.value))
                }
              />
              <FieldError show={hasError("phone")}>
                {formik.errors.phone}
              </FieldError>
            </div>
          </div>
        </section>

        <section className="ac-section">
          <h2 className="jds-title-sm ac-section-title">Endereço</h2>
          <div className="ac-grid">
            <div className="form-group">
              <Label htmlFor="postal_code" required>
                CEP
              </Label>
              <input
                id="postal_code"
                type="text"
                name="postal_code"
                className={inputClass("postal_code")}
                value={formik.values.postal_code}
                onBlur={evt => chooseCEP(evt.target.value)}
                onChange={evt =>
                  formik.setFieldValue("postal_code", cepMask(evt.target.value))
                }
                maxLength="9"
              />
              <FieldError show={hasError("postal_code")}>
                {formik.errors.postal_code}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="state" required>
                Estado
              </Label>
              <Select
                {...jdsSelectProps}
                inputId="state"
                name="state"
                isSearchable={true}
                isClearable={true}
                options={states}
                className={selectClass("state")}
                onChange={value => {
                  if (value && value !== formik.values.state) {
                    formik.setFieldValue("city", null);
                  }
                  formik.setFieldValue("state", value);

                  if (value) {
                    if (value._load_city) {
                      loadCidades(value.state, value._load_city);
                    } else {
                      loadCidades(value.state);
                    }
                  } else {
                    formik.setFieldValue("city", null);
                    loadCidades("");
                  }
                }}
                value={formik.values.state}
                placeholder="Selecione"
                getOptionLabel={option => option.name}
                getOptionValue={option => option.state}
                noOptionsMessage={() => <span>Sem resultados.</span>}
              />
              <FieldError show={hasError("state")}>
                {formik.errors.state}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="city" required>
                Cidade
              </Label>
              <Select
                {...jdsSelectProps}
                inputId="city"
                name="city"
                isSearchable={true}
                isClearable={true}
                options={cities}
                className={selectClass("city")}
                onChange={value => formik.setFieldValue("city", value)}
                value={formik.values.city}
                placeholder="Selecione"
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                noOptionsMessage={() => <span>Sem resultados.</span>}
              />
              <FieldError show={hasError("city")}>
                {formik.errors.city}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="address" required>
                Endereço
              </Label>
              <input
                id="address"
                type="text"
                name="address"
                className={inputClass("address")}
                value={formik.values.address}
                onChange={formik.handleChange}
              />
              <FieldError show={hasError("address")}>
                {formik.errors.address}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="district" required>
                Bairro
              </Label>
              <input
                id="district"
                type="text"
                name="district"
                className={inputClass("district")}
                value={formik.values.district}
                onChange={formik.handleChange}
              />
              <FieldError show={hasError("district")}>
                {formik.errors.district}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="address_number" required>
                Número
              </Label>
              <input
                id="address_number"
                type="text"
                name="address_number"
                className={inputClass("address_number")}
                value={formik.values.address_number}
                onChange={formik.handleChange}
              />
              <FieldError show={hasError("address_number")}>
                {formik.errors.address_number}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="address_complement">Complemento</Label>
              <input
                id="address_complement"
                type="text"
                name="address_complement"
                className="form-control"
                value={formik.values.address_complement}
                onChange={formik.handleChange}
              />
              <FieldError show={hasError("address_complement")}>
                {formik.errors.address_complement}
              </FieldError>
            </div>
          </div>
        </section>

        <section className="ac-section">
          <h2 className="jds-title-sm ac-section-title">Plano e cobrança</h2>
          <div className="ac-grid">
            <div className="ac-span-all">
              <div className="ac-switch-row">
                <span className="switch switch-primary mr-1">
                  <label>
                    <input
                      type="checkbox"
                      id="is_courtesy"
                      checked={formik.values.is_courtesy}
                      onChange={e => {
                        formik.setFieldValue("is_courtesy", e.target.checked);
                      }}
                    />
                    <span></span>
                  </label>
                </span>
                <label htmlFor="is_courtesy" className="ac-switch-label">
                  Cortesia (sem cobrança)
                </label>
                <JdsTag
                  label={formik.values.is_courtesy ? "Ativada" : "Desativada"}
                  color={formik.values.is_courtesy ? "success" : "neutral"}
                />
              </div>
              <small className="form-text text-muted">
                {formik.values.is_courtesy
                  ? "Não será gerada uma cobrança ou boleto no provedor de pagamento."
                  : "Será gerado um boleto de cobrança no valor do plano."}
              </small>
            </div>

            {formik.values.is_courtesy && (
              <>
                <div className="form-group">
                  <Label htmlFor="courtesy_product" required>
                    Tipo de liberação
                  </Label>
                  <CreatableSelect
                    {...jdsSelectProps}
                    inputId="courtesy_product"
                    options={COURTESY_PRODUCT_OPTIONS}
                    placeholder="Selecione ou digite o tipo de liberação"
                    value={
                      COURTESY_PRODUCT_OPTIONS.find(
                        option => option.value === formik.values.courtesy_product
                      ) ||
                      (formik.values.courtesy_product
                        ? {
                          label: formik.values.courtesy_product,
                          value: formik.values.courtesy_product
                        }
                        : null)
                    }
                    formatCreateLabel={inputValue => `Usar "${inputValue}"`}
                    onChange={option =>
                      formik.setFieldValue(
                        "courtesy_product",
                        option?.value?.trim().toLowerCase() || null
                      )
                    }
                  />
                  <FieldError show={Boolean(formik.errors.courtesy_product)}>
                    {formik.errors.courtesy_product}
                  </FieldError>
                </div>
                <div className="form-group">
                  <Label htmlFor="courtesy_duration" required={props.useJusSub}>
                    Duração{props.useJusSub ? "" : " (dias)"}
                  </Label>
                  {!props.useJusSub && !formik.errors.courtesy_duration && (
                    <small className="form-text text-muted ac-help">
                      Deixe em branco para usar a duração padrão do plano, ou
                      informe de {COURTESY_MIN_DAYS} a {COURTESY_MAX_DAYS} dias.
                    </small>
                  )}
                  {props.useJusSub ? (
                    <select
                      id="courtesy_duration"
                      className="form-control"
                      value={formik.values.courtesy_duration}
                      onChange={e =>
                        formik.setFieldValue("courtesy_duration", e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Selecione a duração
                      </option>
                      {COURTESY_JUSSUB_DURATION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="courtesy_duration"
                      className="form-control"
                      type="number"
                      min={COURTESY_MIN_DAYS}
                      max={COURTESY_MAX_DAYS}
                      step="1"
                      value={formik.values.courtesy_duration}
                      onChange={e =>
                        formik.setFieldValue(
                          "courtesy_duration",
                          e.currentTarget.value
                        )
                      }
                      placeholder="Padrão do plano"
                    />
                  )}
                  <FieldError show={Boolean(formik.errors.courtesy_duration)}>
                    {formik.errors.courtesy_duration}
                  </FieldError>
                </div>
              </>
            )}

            <div className="form-group">
              <Label htmlFor="plan" required>
                Plano
              </Label>
              <Select
                {...jdsSelectProps}
                inputId="plan"
                name="plan"
                options={filteredPlans}
                value={formik.values.plan}
                isLoading={isLoading}
                className={selectClass("plan")}
                onChange={value => {
                  formik.setFieldValue("plan", value);
                  if (!formik.values.is_courtesy) handleOnChangePlan(value);
                }}
                onInputChange={debouncedFilter}
                placeholder="Selecione"
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                noOptionsMessage={() => <span>Sem resultados.</span>}
              />
              <FieldError show={hasError("plan")}>
                {formik.errors.plan}
              </FieldError>
            </div>
            <div className="form-group">
              <Label htmlFor="provider" required>
                Provedor de pagamento
              </Label>
              <input
                id="provider"
                type="text"
                name="provider"
                className="form-control"
                disabled
                value={
                  props.useJusSub
                    ? PAYMENT_PROVIDERS.jussub
                    : formik.values.provider?.label ||
                      PAYMENT_PROVIDERS[formik.values.provider?.id] ||
                      ""
                }
              />
              <FieldError show={hasError("provider")}>
                {formik.errors.provider}
              </FieldError>
            </div>
          </div>
        </section>

        <div className="ac-actions">
          <JdsButton
            disabled={!canSubmit}
            onClick={handleCadastrarClick}
          >
            Cadastrar
          </JdsButton>
        </div>
      </div>
      {isPaidFlowWarningOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Cadastrar cliente com um plano pago?"
          message="Este fluxo cobra o cliente normalmente."
          onConfirm={async () => {
            setIsPaidFlowWarningOpen(false);
            await formik.handleSubmit();
          }}
          onClose={() => setIsPaidFlowWarningOpen(false)}
        >
          <p className="ac-warning">
            Se esse cadastro for uma cortesia, cancele e ative a opção
            &quot;Cortesia (sem cobrança)&quot; antes de cadastrar.
          </p>
        </ModalConfirm>
      )}
    </BlockUi>
  );
}
